import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  TradingAnalysisState, 
  TradingAnalysisResult,
  TradingLevels,
  PineScriptConfig,
  TradingAnalysisError,
  AnalysisStatus,
  AnalysisProgress
} from '../types/trading';
import { extractTradingLevels, detectTradingResponse } from '../services/tradingAnalyzer';
import { generatePineScript, mapTradingLevelsToPineScript } from '../services/pineScriptGenerator';
import { v4 as uuidv4 } from 'uuid';

// Load trading history from localStorage
const loadTradingHistory = (): TradingAnalysisResult[] => {
  try {
    const stored = localStorage.getItem('trading_history');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate the stored data structure
      if (Array.isArray(parsed)) {
        return parsed.filter(item => 
          item && 
          typeof item === 'object' && 
          item.id && 
          item.assetName && 
          item.timestamp
        );
      }
    }
  } catch (error) {
    console.warn('Failed to load trading history from localStorage:', error);
  }
  return [];
};

// Load export settings from localStorage
const loadExportSettings = () => {
  try {
    const stored = localStorage.getItem('trading_export_settings');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        return {
          format: parsed.format || 'txt',
          includeComments: parsed.includeComments !== false,
          assetName: parsed.assetName || 'Trading Asset',
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load trading export settings from localStorage:', error);
  }
  return {
    format: 'txt' as const,
    includeComments: true,
    assetName: 'Trading Asset',
  };
};

// Save trading history to localStorage
const saveTradingHistory = (history: TradingAnalysisResult[]) => {
  try {
    localStorage.setItem('trading_history', JSON.stringify(history));
  } catch (error) {
    console.warn('Failed to save trading history to localStorage:', error);
  }
};

// Save export settings to localStorage
const saveExportSettings = (settings: TradingAnalysisState['exportSettings']) => {
  try {
    localStorage.setItem('trading_export_settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save trading export settings to localStorage:', error);
  }
};

// Initial state
const initialState: TradingAnalysisState = {
  activeAnalysis: null,
  analysisHistory: loadTradingHistory(),
  exportSettings: loadExportSettings(),
  ui: {
    pineScriptModalOpen: false,
    progressModalOpen: false,
    exportModalOpen: false,
  },
};

// Async thunk for trading analysis
export const analyzeTradingResponse = createAsyncThunk(
  'trading/analyzeTradingResponse',
  async (
    {
      aiResponseText,
      assetName,
      analysisId,
    }: {
      aiResponseText: string;
      assetName?: string;
      analysisId?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const id = analysisId || uuidv4();
      
      // Initialize analysis
      dispatch(initializeTradingAnalysis({
        id,
        aiResponseText,
        assetName: assetName || 'Trading Asset',
      }));

      // Start progress tracking
      dispatch(updateTradingProgress({
        step: 'detection',
        status: 'in_progress',
        progress: 10,
      }));

      // Detect if this is a trading response
      const isTradingResponse = detectTradingResponse(aiResponseText);
      if (!isTradingResponse) {
        throw new Error('AI response does not contain trading analysis data');
      }

      dispatch(updateTradingProgress({
        step: 'extraction',
        status: 'in_progress', 
        progress: 30,
      }));

      // Extract trading levels
      const tradingLevels = extractTradingLevels(aiResponseText);
      
      // Validate extracted levels
      if (tradingLevels.swingHigh === 0 || tradingLevels.swingLow === 0) {
        throw new Error('Could not extract swing points from the AI response');
      }

      if (tradingLevels.supportLevels.length === 0 && tradingLevels.resistanceLevels.length === 0) {
        throw new Error('Could not extract support or resistance levels from the AI response');
      }

      dispatch(updateTradingProgress({
        step: 'generation',
        status: 'in_progress',
        progress: 70,
      }));

      // Generate Pine Script with AI response as reference
      const generatedResult = generatePineScript(tradingLevels, aiResponseText);
      
      if (!generatedResult.validation.isValid) {
        throw new Error(`Pine Script generation failed: ${generatedResult.validation.errors.join(', ')}`);
      }

      // Create the config by mapping the trading levels
      const config = mapTradingLevelsToPineScript(tradingLevels);

      const pineScriptResult = {
        script: generatedResult.script,
        config,
        validation: generatedResult.validation,
      };

      dispatch(updateTradingProgress({
        step: 'completion',
        status: 'completed',
        progress: 100,
      }));

      return {
        id,
        tradingLevels,
        pineScriptResult,
        warnings: generatedResult.validation.warnings,
      };
    } catch (error: any) {
      console.error('Trading analysis failed:', error);
      return rejectWithValue({
        message: error.message || 'Trading analysis failed',
        code: error.code || 'TRADING_ANALYSIS_ERROR',
        details: error.details,
      });
    }
  }
);

// Async thunk for generating Pine Script from existing levels
export const generatePineScriptFromLevels = createAsyncThunk(
  'trading/generatePineScriptFromLevels',
  async (
    {
      tradingLevels,
      assetName,
      aiResponseText,
    }: {
      tradingLevels: TradingLevels;
      assetName?: string;
      aiResponseText?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const generatedResult = generatePineScript(tradingLevels, aiResponseText);
      
      if (!generatedResult.validation.isValid) {
        throw new Error(`Pine Script generation failed: ${generatedResult.validation.errors.join(', ')}`);
      }

      // Create the config by mapping the trading levels
      const config = mapTradingLevelsToPineScript(tradingLevels);

      const pineScriptResult = {
        script: generatedResult.script,
        config,
        validation: generatedResult.validation,
      };

      return {
        pineScriptResult,
        assetName: assetName || 'Trading Asset',
      };
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || 'Pine Script generation failed',
        code: 'PINE_SCRIPT_ERROR',
      });
    }
  }
);

// Trading slice
const tradingSlice = createSlice({
  name: 'trading',
  initialState,
  reducers: {
    // Initialize a new trading analysis
    initializeTradingAnalysis: (state, action: PayloadAction<{
      id: string;
      aiResponseText: string;
      assetName: string;
    }>) => {
      const { id, aiResponseText, assetName } = action.payload;
      
      state.activeAnalysis = {
        id,
        aiResponseText,
        assetName,
        status: 'pending',
        progress: {
          detection: { status: 'pending', progress: 0 },
          extraction: { status: 'pending', progress: 0 },
          generation: { status: 'pending', progress: 0 },
          completion: { status: 'pending', progress: 0 },
        },
        startTime: Date.now(),
        estimatedCompletion: Date.now() + (30 * 1000), // 30 seconds
      };

      // Open progress modal
      state.ui.progressModalOpen = true;
    },

    // Update trading analysis progress
    updateTradingProgress: (state, action: PayloadAction<{
      step: string;
      status: AnalysisStatus;
      progress: number;
      estimatedTime?: number;
      error?: string;
    }>) => {
      if (!state.activeAnalysis) return;

      const { step, status, progress, estimatedTime, error } = action.payload;
      
      state.activeAnalysis.progress[step] = {
        status,
        progress,
        estimatedTime,
        error,
      };

      // Update overall analysis status
      if (status === 'error') {
        state.activeAnalysis.status = 'error';
        state.activeAnalysis.error = error;
      } else if (step === 'completion' && status === 'completed') {
        state.activeAnalysis.status = 'completed';
      } else if (state.activeAnalysis.status === 'pending') {
        state.activeAnalysis.status = 'in_progress';
      }
    },

    // Cancel active analysis
    cancelTradingAnalysis: (state) => {
      if (state.activeAnalysis) {
        state.activeAnalysis.status = 'error';
        state.activeAnalysis.error = 'Analysis cancelled by user';
      }
    },

    // Clear active analysis
    clearActiveTradingAnalysis: (state) => {
      state.activeAnalysis = null;
      state.ui.progressModalOpen = false;
      state.ui.pineScriptModalOpen = false;
    },

    // Add analysis to history
    addToTradingHistory: (state, action: PayloadAction<TradingAnalysisResult>) => {
      const analysis = action.payload;
      
      // Remove any existing analysis with the same ID
      state.analysisHistory = state.analysisHistory.filter(
        item => item.id !== analysis.id
      );
      
      // Add to beginning of history
      state.analysisHistory.unshift(analysis);
      
      // Limit history to 50 items
      if (state.analysisHistory.length > 50) {
        state.analysisHistory = state.analysisHistory.slice(0, 50);
      }
      
      // Save to localStorage
      saveTradingHistory(state.analysisHistory);
    },

    // Remove analysis from history
    removeFromTradingHistory: (state, action: PayloadAction<string>) => {
      state.analysisHistory = state.analysisHistory.filter(
        item => item.id !== action.payload
      );
      
      // Save to localStorage
      saveTradingHistory(state.analysisHistory);
    },

    // Clear trading history
    clearTradingHistory: (state) => {
      state.analysisHistory = [];
      
      // Save to localStorage
      saveTradingHistory(state.analysisHistory);
    },

    // Update export settings
    updateTradingExportSettings: (state, action: PayloadAction<Partial<TradingAnalysisState['exportSettings']>>) => {
      state.exportSettings = {
        ...state.exportSettings,
        ...action.payload,
      };
      
      // Save to localStorage
      saveExportSettings(state.exportSettings);
    },

    // UI state management
    setPineScriptModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.pineScriptModalOpen = action.payload;
    },

    setTradingProgressModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.progressModalOpen = action.payload;
    },

    setTradingExportModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.exportModalOpen = action.payload;
    },

    // Reset UI state
    resetTradingUIState: (state) => {
      state.ui = {
        pineScriptModalOpen: false,
        progressModalOpen: false,
        exportModalOpen: false,
      };
    },

    // Set current trading levels for manual analysis
    setCurrentTradingLevels: (state, action: PayloadAction<{
      tradingLevels: TradingLevels;
      assetName: string;
    }>) => {
      const { tradingLevels, assetName } = action.payload;
      
      // Create a new analysis entry for manual levels
      const id = uuidv4();
      state.activeAnalysis = {
        id,
        aiResponseText: 'Manual trading levels input',
        assetName,
        status: 'completed',
        progress: {
          detection: { status: 'completed', progress: 100 },
          extraction: { status: 'completed', progress: 100 },
          generation: { status: 'completed', progress: 100 },
          completion: { status: 'completed', progress: 100 },
        },
        startTime: Date.now(),
        estimatedCompletion: Date.now(),
        results: {
          tradingLevels,
          pineScriptResult: null,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle successful trading analysis
      .addCase(analyzeTradingResponse.fulfilled, (state, action) => {
        const { id, tradingLevels, pineScriptResult, warnings } = action.payload;
        
        if (state.activeAnalysis && state.activeAnalysis.id === id) {
          // Mark analysis as completed
          state.activeAnalysis.status = 'completed';
          state.activeAnalysis.results = {
            tradingLevels,
            pineScriptResult,
          };
          
          // Update all progress steps to completed
          Object.keys(state.activeAnalysis.progress).forEach(step => {
            if (state.activeAnalysis!.progress[step]) {
              state.activeAnalysis!.progress[step].status = 'completed';
              state.activeAnalysis!.progress[step].progress = 100;
            }
          });

          // Add warnings if any
          if (warnings && warnings.length > 0) {
            state.activeAnalysis.warnings = warnings;
          }

          // Add to history
          const historyItem: TradingAnalysisResult = {
            id,
            aiResponseText: state.activeAnalysis.aiResponseText,
            assetName: state.activeAnalysis.assetName,
            tradingLevels,
            pineScriptResult,
            timestamp: Date.now(),
            warnings,
          };
          
          tradingSlice.caseReducers.addToTradingHistory(state, {
            type: 'trading/addToTradingHistory',
            payload: historyItem,
          });

          // Open Pine Script modal
          state.ui.pineScriptModalOpen = true;
          state.ui.progressModalOpen = false;
        }
      })
      // Handle trading analysis failure
      .addCase(analyzeTradingResponse.rejected, (state, action) => {
        if (state.activeAnalysis) {
          state.activeAnalysis.status = 'error';
          const errorMessage = (action.payload as any)?.message || action.error?.message || 'Trading analysis failed';
          state.activeAnalysis.error = errorMessage;
          
          // Mark all progress steps as error
          Object.keys(state.activeAnalysis.progress).forEach(step => {
            if (state.activeAnalysis!.progress[step]) {
              state.activeAnalysis!.progress[step].status = 'error';
              state.activeAnalysis!.progress[step].error = errorMessage;
            }
          });
        }
      })
      // Handle trading analysis start
      .addCase(analyzeTradingResponse.pending, (state) => {
        if (state.activeAnalysis) {
          state.activeAnalysis.status = 'in_progress';
        }
      })
      // Handle Pine Script generation
      .addCase(generatePineScriptFromLevels.fulfilled, (state, action) => {
        const { pineScriptResult, assetName } = action.payload;
        
        if (state.activeAnalysis) {
          if (!state.activeAnalysis.results) {
            // This shouldn't happen, but create a default structure
            state.activeAnalysis.results = {
              tradingLevels: {
                supportLevels: [],
                resistanceLevels: [],
                fibonacciRetracements: {
                  level236: 0,
                  level382: 0,
                  level500: 0,
                  level618: 0,
                  level786: 0,
                },
                fibonacciExtensions: {
                  level1272: 0,
                  level1618: 0,
                },
                swingHigh: 0,
                swingLow: 0,
              },
              pineScriptResult,
            };
          } else {
            state.activeAnalysis.results.pineScriptResult = pineScriptResult;
          }
          state.activeAnalysis.assetName = assetName;
          
          // Open Pine Script modal
          state.ui.pineScriptModalOpen = true;
        }
      })
      .addCase(generatePineScriptFromLevels.rejected, (state, action) => {
        if (state.activeAnalysis) {
          const errorMessage = (action.payload as any)?.message || 'Pine Script generation failed';
          state.activeAnalysis.error = errorMessage;
        }
      });
  },
});

// Export actions
export const {
  initializeTradingAnalysis,
  updateTradingProgress,
  cancelTradingAnalysis,
  clearActiveTradingAnalysis,
  addToTradingHistory,
  removeFromTradingHistory,
  clearTradingHistory,
  updateTradingExportSettings,
  setPineScriptModalOpen,
  setTradingProgressModalOpen,
  setTradingExportModalOpen,
  resetTradingUIState,
  setCurrentTradingLevels,
} = tradingSlice.actions;

// Selectors
export const selectTradingAnalysis = (state: { trading: TradingAnalysisState }) => 
  state.trading;

export const selectActiveTradingAnalysis = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis;

export const selectTradingHistory = (state: { trading: TradingAnalysisState }) => 
  state.trading.analysisHistory;

export const selectTradingProgress = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis?.progress || {};

export const selectTradingResults = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis?.results;

export const selectTradingExportSettings = (state: { trading: TradingAnalysisState }) => 
  state.trading.exportSettings;

export const selectTradingUIState = (state: { trading: TradingAnalysisState }) => 
  state.trading.ui;

export const selectIsTradingAnalysisActive = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis?.status === 'in_progress';

export const selectTradingAnalysisById = (state: { trading: TradingAnalysisState }, id: string) => 
  state.trading.analysisHistory.find(analysis => analysis.id === id);

export const selectCurrentTradingLevels = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis?.results?.tradingLevels;

export const selectCurrentPineScript = (state: { trading: TradingAnalysisState }) => 
  state.trading.activeAnalysis?.results?.pineScriptResult;

// Export reducer
export default tradingSlice.reducer;